/**
 * Utility functions for extracting and displaying creative resolution/media owner information
 */

export interface CreativeInfo {
  width?: number;
  height?: number;
  label: string;
  mediaOwner: string;
  orientation?: 'landscape' | 'portrait' | 'square';
}

// Known resolutions mapped to media owners
const RESOLUTION_MAP: Record<string, { mediaOwner: string; label: string }> = {
  '1920x1080': { mediaOwner: 'Alan', label: 'Landscape 1920×1080' },
  '1080x1920': { mediaOwner: 'Alan', label: 'Portrait 1080×1920' },
  '2225x3562': { mediaOwner: 'Waki', label: 'Waki 2225×3562' },
  '1080x920': { mediaOwner: 'TableAds', label: 'Standard 1080×920' },
};

// Known media owners and their expected screen orientations
const MEDIA_OWNER_INFO: Record<string, { label: string; orientation?: 'landscape' | 'portrait' }> = {
  'Alan-landscape': { label: 'Alan (Landscape)', orientation: 'landscape' },
  'Alan-portrait': { label: 'Alan (Portrait)', orientation: 'portrait' },
  'Alan': { label: 'Alan' },
  'TableAds': { label: 'TableAds', orientation: 'landscape' },
  'Waki': { label: 'Waki', orientation: 'portrait' },
};

/**
 * Extract media owner from creative URL filename
 * Filename patterns like:
 * - 1750424540970-TableAds-WhatsApp...
 * - 1750424545250-Alan-landscape-WhatsApp...
 * - 1750424552227-Alan-portrait-WhatsApp...
 * - 1750424557735-Waki-WhatsApp...
 */
export function extractMediaOwnerFromUrl(url: string): string | null {
  if (!url) return null;
  
  try {
    const decodedUrl = decodeURIComponent(url);
    const filename = decodedUrl.split('/').pop() || '';
    
    // Pattern to match: timestamp-MediaOwner- or timestamp-MediaOwner-orientation-
    // Check for Alan with orientation first
    if (filename.includes('-Alan-landscape-') || filename.toLowerCase().includes('alan-landscape')) {
      return 'Alan-landscape';
    }
    if (filename.includes('-Alan-portrait-') || filename.toLowerCase().includes('alan-portrait')) {
      return 'Alan-portrait';
    }
    
    // Check for known media owners
    const mediaOwners = ['TableAds', 'Waki', 'Alan'];
    for (const owner of mediaOwners) {
      if (filename.includes(`-${owner}-`) || filename.toLowerCase().includes(owner.toLowerCase())) {
        return owner;
      }
    }
    
    // Try to extract location name from pattern: timestamp-LocationName-filename
    // E.g., "1768303198051-2_-_Building-Mounted_Screens_Medina-WhatsApp..."
    // Pattern: digits followed by dash, then location info, then another dash and original filename
    const locationMatch = filename.match(/^\d+-(.+?)-[A-Za-z]/);
    if (locationMatch && locationMatch[1]) {
      // Clean up the location name
      let locationName = locationMatch[1]
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .trim();
      
      // Remove leading numbers like "2 - " or "1 - "
      locationName = locationName.replace(/^\d+\s*[-_]?\s*/, '').trim();
      
      if (locationName && locationName.length > 2) {
        return locationName;
      }
    }
    
    return null;
  } catch (e) {
    console.error('Error extracting media owner from URL:', e);
    return null;
  }
}

/**
 * Extract resolution information from a creative URL
 * Attempts to parse resolution from filename patterns like:
 * - creative_1920x1080_timestamp.jpg
 * - landscape_1920x1080.mp4
 * - 1080x1920_portrait.png
 */
export function extractResolutionFromUrl(url: string): CreativeInfo | null {
  if (!url) return null;
  
  try {
    const decodedUrl = decodeURIComponent(url);
    const filename = decodedUrl.split('/').pop() || '';
    
    // First try to extract media owner from filename pattern
    const mediaOwner = extractMediaOwnerFromUrl(url);
    if (mediaOwner) {
      const info = MEDIA_OWNER_INFO[mediaOwner];
      return {
        label: info?.label || mediaOwner,
        mediaOwner: mediaOwner.replace('-landscape', '').replace('-portrait', ''),
        orientation: info?.orientation,
      };
    }
    
    // Fall back to resolution pattern: WIDTHxHEIGHT
    const resolutionPattern = /(\d{3,4})x(\d{3,4})/i;
    const match = filename.match(resolutionPattern);
    
    if (match) {
      const width = parseInt(match[1], 10);
      const height = parseInt(match[2], 10);
      const key = `${width}x${height}`;
      
      const knownResolution = RESOLUTION_MAP[key];
      const orientation = width > height ? 'landscape' : width < height ? 'portrait' : 'square';
      
      return {
        width,
        height,
        label: knownResolution?.label || `${width}×${height}`,
        mediaOwner: knownResolution?.mediaOwner || 'Unknown',
        orientation,
      };
    }
    
    return null;
  } catch (e) {
    console.error('Error extracting resolution from URL:', e);
    return null;
  }
}

/**
 * Get a display label for a creative URL
 */
export function getResolutionLabel(url: string): string {
  const info = extractResolutionFromUrl(url);
  return info?.label || '';
}

/**
 * Get the media owner based on URL
 */
export function getMediaOwnerFromResolution(url: string): string {
  const info = extractResolutionFromUrl(url);
  return info?.mediaOwner || '';
}

/**
 * Get badge color based on media owner
 */
export function getResolutionBadgeColor(url: string): string {
  const info = extractResolutionFromUrl(url);
  
  if (!info || !info.mediaOwner) return 'bg-muted text-muted-foreground';
  
  switch (info.mediaOwner) {
    case 'Alan':
      return info.orientation === 'landscape' 
        ? 'bg-blue-100 text-blue-700 border-blue-200'
        : 'bg-purple-100 text-purple-700 border-purple-200';
    case 'Waki':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'TableAds':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/**
 * Group creative URLs by media owner
 */
export function groupCreativesByMediaOwner(urls: string[]): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};
  
  for (const url of urls) {
    const mediaOwner = extractMediaOwnerFromUrl(url) || 'Other';
    // Normalize Alan variants to include orientation in the key
    const key = mediaOwner;
    
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(url);
  }
  
  return grouped;
}

/**
 * Get a friendly display name for a media owner key
 */
export function getMediaOwnerDisplayName(key: string): string {
  const info = MEDIA_OWNER_INFO[key];
  return info?.label || key;
}
